import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { PageHeader, PageBody, Field, StatusPill, statusTone } from "@/components/PageChrome";
import { Input, Select, Button } from "@/components/controls";
import ImageInput from "@/components/ImageInput";
import { Plus, X, Search, ArrowRight, Truck, PackageOpen, History, Package, Filter, Upload, Download, AlertTriangle, Split, Copy, ChevronRight, ChevronDown, Layers } from "lucide-react";
import { T } from "@/lib/testIds";
import { usePerms, useAuth } from "@/lib/auth";
import SkuTimelineTrends from "@/components/SkuTimelineTrends";
import FieldFilterBar, { applyFieldFilters } from "@/components/FieldFilterBar";
import PaginationBar from "@/components/PaginationBar";
import FilterPresets from "@/components/FilterPresets";
import ForecastButton from "@/components/ForecastButton";
import MultiSelectFilter from "@/components/MultiSelectFilter";
import InlineCell from "@/components/InlineCell";
import ImageThumb from "@/components/ImageThumb";
import { TranslateInline } from "@/components/TranslateInline";

const STATUSES = ["Eligible", "OTS", "Added", "Dispatched", "Delivered", "Cancelled"];

const PROCUREMENT_FILTER_FIELDS = [
  { key: "system_id", label: "System ID", type: "text" },
  // Aug 1 2026 — user asked for China No. / Source URL / Source as multi-select
  // filters ("china id, source url should be a select filter in procurement
  // module with option of multi select and blank/empty select"). Options are
  // derived from the loaded rows, see `filterDropdowns` below.
  { key: "china_id", label: "China No.", type: "select", opts: (d) => d.china_id || [] },
  // Feb 27 2026 — Source filter with 3 options: Order (order-linked row),
  // Direct (standalone air/regular), Sea (standalone sea-freight). Derived
  // from the row's `source` classifier + `procurement_source` — see
  // `withSourceLabel` below where every row gains a `source_label`.
  { key: "source_label", label: "Source", type: "select", opts: () => ["Direct", "Order", "Sea"] },
  { key: "source_url", label: "Source URL", type: "select", opts: (d) => d.source_url || [] },
  { key: "system_sku", label: "System SKU", type: "text" },
  { key: "product_name", label: "Product Name", type: "text" },
  { key: "marketplace_identifier", label: "Marketplace ID", type: "text" },
  { key: "marketplace", label: "Marketplace", type: "select", opts: (d) => d.marketplace || [] },
  { key: "account_id", label: "Account", type: "select", opts: (d) => d.account_id || [] },
  { key: "china_order_status", label: "Status", type: "select", opts: (d) => d.china_order_status || STATUSES },
  { key: "china_order_id", label: "China Order ID", type: "text" },
  // Sep 20 2026 — convert Shipment ID to a multi-select filter (search
  // + blank/empty toggle handled by FieldFilterBar). Options derived
  // from loaded rows via `filterDropdowns.china_shipment_id` below.
  { key: "china_shipment_id", label: "Shipment ID", type: "select", opts: (d) => d.china_shipment_id || [] },
  { key: "variant", label: "Variant", type: "text" },
  { key: "china_qty_to_order", label: "Qty", type: "qty" },
  { key: "shipped_qty", label: "Shipped Qty", type: "qty" },  // Mar 2 2026
  { key: "china_price", label: "China Price ¥", type: "number" },
  // Feb 27 2026 — Sea-freight price alongside standard China Price.
  { key: "sea_price", label: "Sea Price ¥", type: "number" },
  { key: "china_shipping", label: "Shipping ¥", type: "number" },
  { key: "china_weight_gram", label: "Weight (g)", type: "number" },
  { key: "china_transfer_price", label: "Transfer Price ₹", type: "number" },
  { key: "china_order_date", label: "Order Date", type: "date" },
  { key: "china_order_dispatched_date", label: "Dispatched", type: "date" },
  { key: "china_order_received_date", label: "Received", type: "date" },
  { key: "china_comments", label: "Comments", type: "text" },
  // Mar 3 2026 — Tax metadata (name / rate / HSN). Available as filter,
  // bulk-edit, and inline column.
  { key: "tax_name", label: "Tax Name", type: "text" },
  { key: "tax_rate", label: "Tax Rate %", type: "number" },
  { key: "hsn_code", label: "HSN", type: "text" },
  // Mar 3 2026 — new costing / logistics fields.
  { key: "china_return_price", label: "Return Price ¥", type: "number" },
  { key: "total_price", label: "Total Price ¥", type: "number" },
  { key: "package_no", label: "Package No", type: "text" },
  { key: "delivered_date", label: "Delivered Date", type: "date" },
  // Sep 7 2026 — expose the remaining PROC_COLS fields so admins can
  // reveal them via the column picker. Keeps the picker aligned with
  // what ships in the export.
  { key: "product_type", label: "Product Type", type: "text" },
  { key: "warehouse_name", label: "Warehouse", type: "text" },
  { key: "image_url", label: "Image URL", type: "text" },
  { key: "moq", label: "MOQ", type: "number" },
  { key: "electrical_type", label: "Electrical Type", type: "text" },
  { key: "gst_rate", label: "GST Rate %", type: "number" },
  { key: "global_rmb_conversion_rate", label: "RMB→INR Rate", type: "number" },
  { key: "international_freight_rate", label: "Intl Freight Rate", type: "number" },
  { key: "aggregator_commission_percentage", label: "Aggregator Comm %", type: "number" },
  { key: "china_margin_percentage", label: "China Margin %", type: "number" },
  { key: "china_reseller_margin_percentage", label: "Reseller Margin %", type: "number" },
  { key: "china_costing", label: "China Costing", type: "number" },
  { key: "china_international_freight_charges", label: "Intl Freight ₹", type: "number" },
  { key: "china_aggregator_margin", label: "Aggregator Margin ₹", type: "number" },
  { key: "china_gst", label: "China GST ₹", type: "number" },
  { key: "china_landing_price", label: "Landing Price ₹", type: "number" },
  { key: "china_margin", label: "China Margin ₹", type: "number" },
  { key: "china_margin_gst", label: "Margin GST ₹", type: "number" },
];

const emptyRow = () => ({
  system_sku: "", product_name: "", source_url: "", variant: "",
  china_price: "", sea_price: "", china_shipping: "", china_weight_gram: "",
  china_qty_to_order: 1, shipped_qty: 1, china_order_status: "Eligible",
  china_order_id: "", china_shipment_id: "",
  china_order_date: new Date().toISOString().slice(0, 10),
  china_comments: "", image_url: "", procurement_source: "Direct",
});

// Mar 3 2026 — dd-Mmm-yyyy formatter used across every date field in the
// Procurement module (list, edit modal, filter chips, inline cells). Accepts
// "YYYY-MM-DD", ISO with time, or already-formatted values; returns "" on
// bad input so tables render blank instead of "Invalid Date".
function fmtDate(v) {
  if (v === null || v === undefined || v === "") return "";
  const s = String(v).trim();
  // Already dd-Mmm-yyyy? leave it alone.
  if (/^\d{2}-[A-Za-z]{3}-\d{4}$/.test(s)) return s;
  const d = new Date(s.length === 10 ? `${s}T00:00:00` : s);
  if (Number.isNaN(d.getTime())) return "";
  const months = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
  const dd = String(d.getDate()).padStart(2, "0");
  return `${dd}-${months[d.getMonth()]}-${d.getFullYear()}`;
}

// Mar 3 2026 — sort dropdown options. Backend accepts the ``sort_by`` key.
const SORT_OPTIONS = [
  { value: "", label: "Default (newest first)" },
  { value: "china_id_desc",       label: "China ID (High to Low)" },
  { value: "china_id_asc",        label: "China ID (Low to High)" },
  { value: "qty_desc",            label: "Qty (High to Low)" },
  { value: "qty_asc",             label: "Qty (Low to High)" },
  { value: "china_price_desc",    label: "China Price (High to Low)" },
  { value: "china_price_asc",     label: "China Price (Low to High)" },
  { value: "china_shipping_desc", label: "China Shipping (High to Low)" },
  { value: "china_shipping_asc",  label: "China Shipping (Low to High)" },
  { value: "total_price_desc",    label: "Total Price (High to Low)" },
  { value: "total_price_asc",     label: "Total Price (Low to High)" },
  { value: "weight_desc",         label: "Weight (High to Low)" },
  { value: "weight_asc",          label: "Weight (Low to High)" },
  { value: "order_date_desc",     label: "Order Date (New to Old)" },
  { value: "order_date_asc",      label: "Order Date (Old to New)" },
  { value: "dispatched_desc",     label: "Dispatched Date (New to Old)" },
  { value: "dispatched_asc",      label: "Dispatched Date (Old to New)" },
  { value: "received_desc",       label: "Received Date (New to Old)" },
  { value: "received_asc",        label: "Received Date (Old to New)" },
  { value: "delivered_desc",      label: "Delivered Date (New to Old)" },
  { value: "delivered_asc",       label: "Delivered Date (Old to New)" },
];

export default function Procurement() {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);
  const [skuTimeline, setSkuTimeline] = useState(null);
  const [showFilterBar, setShowFilterBar] = useState(false);
  const [advFilters, setAdvFilters] = useState({});
  const [dropdowns, setDropdowns] = useState({});
  const [accounts, setAccounts] = useState([]);
  const [revealedCols, setRevealedCols] = useState(() => {
    try { return JSON.parse(localStorage.getItem("procurement_reveal_cols") || "[]"); }
    catch { return []; }
  });
  const toggleRevealCol = (key) => {
    setRevealedCols((cur) => {
      const next = cur.includes(key) ? cur.filter((k) => k !== key) : [...cur, key];
      localStorage.setItem("procurement_reveal_cols", JSON.stringify(next));
      return next;
    });
  };
  const { canView, canEdit, canBulkEdit, allowedWarehouses, canFeatureExplicit } = usePerms();
  // Aug 5 2026 — this used to read canEdit("orders") alone, so granting a team
  // the procurement module (or the procurement import permission) never revealed
  // the New / Import buttons. Deliberately additive: dropping the orders check
  // would REMOVE the buttons from teams that hold orders.edit but not
  // procurement.edit, which is how most existing teams here are configured.
  const editable = canEdit("procurement") || canEdit("orders");
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  // Mar 3 2026 — Simplified Procurement Access. Opt-in feature flag.
  // When on: form hides costing inputs + shows plain transfer_price input;
  // system_sku becomes optional; import downloads simplified template.
  const isSimplified = !isAdmin && canFeatureExplicit("simplified_procurement");
  // Small helper — copy any string to clipboard with toast feedback.
  const copyToClipboard = async (text, label = "Value") => {
    if (!text) return;
    try {
      await navigator.clipboard.writeText(String(text));
      toast.success(`${label} copied`);
    } catch {
      toast.error("Copy failed");
    }
  };
  // Bulk-delete state (parity with Orders module).
  // Only standalone rows can be selected — order-linked rows must be deleted
  // via the Orders module because they actually live in `orders`.
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [confirmDeleteSelected, setConfirmDeleteSelected] = useState(false);
  const [confirmDeleteText, setConfirmDeleteText] = useState("");
  const [deletingSelected, setDeletingSelected] = useState(false);
  // Mar 1 2026 — top-bar Warehouse multi-select filter (server-side, pushed as
  // ?warehouse_name=BLR,NOIDA — see routes_procurement.list_procurement).
  const [warehouseFilter, setWarehouseFilter] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  // Mar 1 2026 — Wide/Compact toolbar toggle (mirrors Orders module). Compact
  // hides the 4 "detail" columns (China Shipping, China Order ID, Dispatched,
  // Received) so the base grid stays a 12-col overview; Wide reveals them.
  const [density, setDensity] = useState(() => localStorage.getItem("procurement_density") || "compact");
  const setDensityPersist = (d) => { setDensity(d); localStorage.setItem("procurement_density", d); };
  // Mar 1 2026 — Bulk Update bar (mirrors Orders). Permission-gated via
  // canBulkEdit("procurement") which is granted per-team via the Teams matrix
  // (or per-user override). Same 3-input pattern as Orders: field selector +
  // value input + mode toggle (Update all / Fill blanks only). The scope
  // is always the CURRENT filter set — never the whole 5k-row dataset.
  const [bulkField, setBulkField] = useState("");
  const [bulkValue, setBulkValue] = useState("");
  const [bulkMode, setBulkMode] = useState("all");
  const [bulkRunning, setBulkRunning] = useState(false);
  const [bulkConfirm, setBulkConfirm] = useState(null);

  // Mar 2 2026 — Split-shipment modal state. `splitFor` is the row being
  // split; `splitDrafts` is the list of new (shipment_id, qty) rows the user
  // is building up. See the SplitShipmentModal render below the table.
  const [splitFor, setSplitFor] = useState(null);
  const [splitDrafts, setSplitDrafts] = useState([]);
  const [splitBusy, setSplitBusy] = useState(false);

  const openSplitModal = (row) => {
    if (!row) return;
    // Seed with 2 splits: first mirrors the current shipment/qty so the
    // user only has to change one side to fire off a valid split.
    const qty = Number(row.china_qty_to_order || 0);
    setSplitFor(row);
    setSplitDrafts([
      { china_shipment_id: row.china_shipment_id || "", quantity: qty },
      { china_shipment_id: "", quantity: 0 },
    ]);
  };

  const addSplitDraft = () => setSplitDrafts((prev) => [...prev, { china_shipment_id: "", quantity: 0 }]);
  const removeSplitDraft = (idx) => setSplitDrafts((prev) => prev.filter((_, i) => i !== idx));
  const updateSplitDraft = (idx, patch) =>
    setSplitDrafts((prev) => prev.map((d, i) => (i === idx ? { ...d, ...patch } : d)));

  const submitSplit = async () => {
    if (!splitFor) return;
    if (splitFor.source !== "standalone") {
      toast.error("This row comes from an order and can't be split here. Add the extra quantity as a standalone procurement row instead.");
      return;
    }
    const originalQty = Number(splitFor.china_qty_to_order || 0);
    const sumQty = splitDrafts.reduce((s, d) => s + Number(d.quantity || 0), 0);
    if (splitDrafts.length < 2) { toast.error("Add at least 2 splits"); return; }
    if (splitDrafts.some((d) => !String(d.china_shipment_id || "").trim())) {
      toast.error("Every split needs a shipment ID"); return;
    }
    if (splitDrafts.some((d) => Number(d.quantity || 0) <= 0)) {
      toast.error("Every split needs a quantity > 0"); return;
    }
    if (Math.abs(sumQty - originalQty) > 1e-6) {
      toast.error(`Quantities sum to ${sumQty} but the original row is ${originalQty}. They must match.`);
      return;
    }
    setSplitBusy(true);
    try {
      const { data } = await api.post(`/procurement/${splitFor.id}/split`, {
        splits: splitDrafts.map((d) => ({
          china_shipment_id: String(d.china_shipment_id).trim(),
          quantity: Number(d.quantity),
        })),
      });
      toast.success(`Split into ${1 + data.created_count} rows (${data.created_count} new)`);
      setSplitFor(null); setSplitDrafts([]);
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
    setSplitBusy(false);
  };

  useEffect(() => {
    api.get("/settings/dropdowns").then((r) => setDropdowns(r.data)).catch(() => {});
    api.get("/accounts").then((r) => setAccounts(r.data || [])).catch(() => {});
    api.get("/warehouse").then((r) => {
      const items = Array.isArray(r.data) ? r.data : (r.data?.items || []);
      setWarehouses(items);
    }).catch(() => {});
  }, []);

  // Merge accounts into the dropdown map so account_id renders as a select.
  // Aug 1 2026 — China No. / Source / Source URL have no settings-managed
  // dropdown, so their option lists are derived from the rows on screen.
  const filterDropdowns = useMemo(() => ({
    ...dropdowns,
    account_id: [...new Set(accounts.map((a) => a.account_id).filter(Boolean))].sort(),
    china_id: [...new Set(rows.map((r) => r.china_id)
      .filter((v) => v !== null && v !== undefined && v !== ""))]
      .sort((a, b) => Number(a) - Number(b)),
    source: [...new Set(rows.map((r) => r.source).filter(Boolean))].sort(),
    source_url: [...new Set(rows.map((r) => r.source_url).filter(Boolean))].sort(),
    // Sep 20 2026 — Shipment ID multi-select filter. Distinct values
    // derived from the loaded rowset (same pattern as china_id /
    // source_url); the blank/empty toggle in FieldFilterBar covers
    // rows with no shipment_id assigned yet.
    china_shipment_id: [...new Set(rows.map((r) => r.china_shipment_id).filter(Boolean))].sort(),
    // Feb 27 2026 — Source (user-selected Direct / Sea) — settings dropdown
    // is authoritative; merge in any distinct row values so custom options
    // ops added via Settings + ad-hoc values from the data both appear.
    procurement_source: (() => {
      const seed = dropdowns.procurement_source && dropdowns.procurement_source.length
        ? dropdowns.procurement_source
        : ["Direct", "Sea"];
      const fromRows = rows.map((r) => r.procurement_source).filter(Boolean);
      return [...new Set([...seed, ...fromRows])];
    })(),
  }), [dropdowns, accounts, rows]);

  const doExport = () => {
    const API = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
    const token = localStorage.getItem("access_token");
    fetch(`${API}/api/procurement/export/csv`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => { if (!r.ok) throw new Error("Download failed"); return r.blob(); })
      .then((blob) => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url; a.download = "procurement.csv"; a.click();
        URL.revokeObjectURL(url);
      });
  };
  const doTemplate = () => {
    const API = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
    const token = localStorage.getItem("access_token");
    const url = `${API}/api/procurement/template/csv${isSimplified ? "?simplified=true" : ""}`;
    const fname = isSimplified ? "procurement_template_simplified.csv" : "procurement_template.csv";
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => { if (!r.ok) throw new Error("Download failed"); return r.blob(); })
      .then((blob) => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url; a.download = fname; a.click();
        URL.revokeObjectURL(url);
      });
  };
  const doImport = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    const fd = new FormData(); fd.append("file", f);
    try {
      const { data } = await api.post("/procurement/import/csv", fd, { headers: { "Content-Type": "multipart/form-data" } });
      // Report skipped rows and errors too. Previously only created/updated
      // were shown, so rows the import refused to act on vanished silently and
      // an import that did nothing looked identical to one that worked.
      const bits = [`${data.created} created`, `${data.updated} updated`];
      if (data.skipped) bits.push(`${data.skipped} skipped`);
      const nErr = data.total_errors ?? (data.errors || []).length;
      if (nErr) bits.push(`${nErr} error${nErr === 1 ? "" : "s"}`);
      const msg = `Import: ${bits.join(", ")}`;
      if (nErr || data.skipped) {
        const first = (data.errors || [])[0];
        toast.warning(first ? `${msg} — row ${first.row}: ${first.reason}` : msg);
      } else {
        toast.success(msg);
      }
      await load();
    } catch (err) { toast.error(formatApiError(err)); }
    e.target.value = "";
  };
  const doWipe = async () => {
    const c = window.prompt('Type "WIPE PROCUREMENT" to permanently delete every standalone procurement row.');
    if (c !== "WIPE PROCUREMENT") return;
    try {
      const { data } = await api.post("/procurement/admin/wipe", { confirm: c });
      toast.success(`Wiped ${data.deleted} row(s)`);
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const [sortBy, setSortBy] = useState("");  // Mar 3 2026 — sort dropdown state
  // Feb 27 2026 — derive the unified Source label (Order / Direct / Sea)
  // from the row's classifier + user-selected procurement_source. Filter,
  // column, and chip all read `source_label` so the three surfaces stay
  // in lock-step.
  const _sourceLabel = (r) => {
    if (!r) return "";
    if (r.source === "order") return "Order";
    // Sep 19 2026 (ticket #258251) — an allocated buy row created from an
    // order is stored in the procurement collection (source "standalone"
    // for edit-permission purposes) but still carries procurement_source
    // "Order". That was falling through to "Direct" below, making a
    // correctly-linked row look like a manual entry.
    if (r.procurement_source === "Order") return "Order";
    return r.procurement_source === "Sea" ? "Sea" : "Direct";
  };
  const _withSourceLabel = (r) => ({ ...r, source_label: _sourceLabel(r) });

  const load = async () => {
    try {
      const params = {};
      if (q) params.q = q;
      if (statusFilter) params.status = statusFilter;
      if (warehouseFilter.length) params.warehouse_name = warehouseFilter.join(",");
      if (sortBy) params.sort_by = sortBy;  // Mar 3 2026 — server sort
      const { data } = await api.get("/procurement", { params });
      setRows((data || []).map(_withSourceLabel));
    } catch (e) { toast.error(formatApiError(e)); }
  };

  useEffect(() => { load(); }, [statusFilter, warehouseFilter, sortBy]); // eslint-disable-line

  /**
   * Mar 1 2026 — inline-edit save. Called by every <InlineCell> in the table.
   * PATCHes just the one field to the correct backend (procurement.PATCH for
   * standalone rows, orders.PATCH for order-linked rows), applies an
   * optimistic in-place row mutation, and reconciles with the server
   * response so any recomputed derived fields (transfer price, margin) also
   * update without reloading the whole grid.
   */
  const saveInline = async (row, field, newValue) => {
    // Optimistic client mutation so the UI feels instant.
    setRows((rs) => rs.map((r) =>
      r.id === row.id && r.source === row.source ? _withSourceLabel({ ...r, [field]: newValue }) : r
    ));
    try {
      const path = row.source === "order" ? `/orders/${row.id}` : `/procurement/${row.id}`;
      const { data } = await api.patch(path, { [field]: newValue });
      // Reconcile with server response (picks up derived-field recomputes).
      setRows((rs) => rs.map((r) => (r.id === row.id && r.source === row.source
        ? _withSourceLabel({ ...r, ...data, source: r.source })
        : r)));
    } catch (e) {
      toast.error(formatApiError(e));
      // Roll back on failure so the cell reflects the persisted value.
      setRows((rs) => rs.map((r) =>
        r.id === row.id && r.source === row.source ? _withSourceLabel({ ...r, [field]: row[field] }) : r
      ));
    }
  };

  // Mar 1 2026 — Bulk Update: same 12-field spec as InlineCell. Kept
  // side-by-side with saveInline() so the two edit surfaces stay in lock-step.
  const BULK_FIELD_SPECS = {
    china_order_status: { type: "select", options: dropdowns.china_order_status || STATUSES },
    china_qty_to_order: { type: "number" },
    shipped_qty:        { type: "number" },  // Mar 2 2026 — bulk-editable
    china_price:        { type: "number" },
    sea_price:          { type: "number" },
    china_shipping:     { type: "number" },
    china_order_id:     { type: "text" },
    china_weight_gram:  { type: "number" },
    china_shipment_id:  { type: "text" },
    china_order_date:            { type: "date" },
    china_order_dispatched_date: { type: "date" },
    china_order_received_date:   { type: "date" },
    image_url:      { type: "text" },
    china_comments: { type: "text" },
    // Mar 3 2026 — tax metadata bulk-editable.
    tax_name:  { type: "text" },
    tax_rate:  { type: "number" },
    hsn_code:  { type: "text" },
    // Mar 3 2026 — new fields (total_price excluded — read-only, computed).
    china_return_price: { type: "number" },
    package_no:         { type: "text" },
    delivered_date:     { type: "date" },
  };
  const BULK_FIELD_LABELS = {
    china_order_status: "Status",
    china_qty_to_order: "Qty",
    shipped_qty: "Shipped Qty",
    china_price: "China Price",
    sea_price: "Sea Price ¥",
    china_shipping: "China Shipping",
    china_order_id: "China Order ID",
    china_weight_gram: "Weight",
    china_shipment_id: "Shipment ID",
    china_order_date: "Order Date",
    china_order_dispatched_date: "Dispatched Date",
    china_order_received_date: "Received Date",
    image_url: "Image URL",
    china_comments: "Comments",
    tax_name: "Tax Name",
    tax_rate: "Tax Rate %",
    hsn_code: "HSN",
    china_return_price: "Return Price ¥",
    package_no: "Package No",
    delivered_date: "Delivered Date",
  };
  const _isBlankValue = (v) =>
    v === null || v === undefined || v === "" ||
    (typeof v === "number" && Number.isNaN(v));

  const runBulkPreview = () => {
    if (!bulkField) return;
    // Sep 25 2026 (ticket #258251) -- was hard-blocking "Update all" with a
    // blank target value entirely, with no way to actually clear a field in
    // bulk (the "Fill blanks only" alternative only ever matches
    // already-blank rows, so blanking there is a no-op). Customer spec:
    // bulk edit must accept blank, same as a single inline edit already can.
    // The confirmation step below already renders the target as "(blank)"
    // in bold before anything is applied, which is the safety net here.
    // Scope = currently visible rows (post filter + search + status tab).
    let candidates = filteredRows;
    if (bulkMode === "blanks") {
      candidates = candidates.filter((r) => _isBlankValue(r[bulkField]));
    }
    const ids = candidates.map((r) => r.id).filter(Boolean);
    setBulkConfirm({ count: ids.length, ids, mode: bulkMode });
  };
  const runBulkApply = async () => {
    if (!bulkConfirm) return;
    setBulkRunning(true);
    try {
      const { data } = await api.post("/procurement/admin/bulk-update", {
        ids: bulkConfirm.ids,
        field: bulkField,
        value: bulkValue,
      });
      toast.success(
        `Updated ${data.updated} row${data.updated === 1 ? "" : "s"}.` +
        (data.failed ? ` ${data.failed} failed.` : "")
      );
      setBulkConfirm(null);
      setBulkField(""); setBulkValue("");
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBulkRunning(false); }
  };


  const counts = useMemo(() => {
    const c = { total: rows.length };
    (dropdowns.china_order_status || STATUSES).forEach((s) => { c[s] = rows.filter((r) => r.china_order_status === s).length; });
    return c;
  }, [rows]);

  // Client-side advanced filter pass
  const filteredRows = useMemo(
    () => rows.filter((r) => applyFieldFilters(r, advFilters, PROCUREMENT_FILTER_FIELDS)),
    [rows, advFilters]
  );

  // Aug 1 2026 — pagination. Paging is client-side because the filter pass
  // above is: server-side paging would filter only the visible page.
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  // Any change to the filter inputs sends you back to page 1, otherwise you
  // can end up parked on page 6 of a 2-page result.
  useEffect(() => { setPage(1); }, [advFilters, q, statusFilter]);
  const pagedRows = useMemo(
    () => filteredRows.slice((page - 1) * pageSize, page * pageSize),
    [filteredRows, page, pageSize]
  );

  // Sep 7 2026 (ticket #245038) — customer explicitly asked for the grouped-
  // summary display (originally built for #241179/#243978) to go away
  // entirely: "keep the UI like this only", pointing at a normal, ungrouped
  // row. Every row now gets its own unique key so pageGroups below always
  // takes the single-row render path — no "(N orders · qty X) GROUPED BY
  // CHINA ID" summary is shown.
  const groupKeyInfo = (r) => ({ key: `row:${r.source}-${r.id}`, label: null });
  const pageGroups = useMemo(() => {
    const map = new Map();
    const order = [];
    for (const r of pagedRows) {
      const { key, label } = groupKeyInfo(r);
      if (!map.has(key)) { map.set(key, { key, label, rows: [] }); order.push(key); }
      map.get(key).rows.push(r);
    }
    return order.map((k) => map.get(k));
  }, [pagedRows]);

  const activeAdvCount = Object.keys(advFilters).filter((k) => {
    const v = advFilters[k];
    if (!v) return false;
    if (typeof v === "object") return Object.values(v).some((x) => x !== "" && x !== undefined);
    return true;
  }).length;

  const save = async () => {
    if (!editing) return;
    // Sep 18 2026 — Direct / Sea rows must carry a warehouse so the
    // In-Transit aggregation can attribute this row to the correct
    // warehouse. Order-type rows inherit warehouse from the account, so
    // this check applies only to standalone rows.
    const _ps = (editing.procurement_source || "").trim();
    const isStandalone = editing.source === "standalone" || !editing.id;
    if (isStandalone && (_ps === "Direct" || _ps === "Sea")
        && !((editing.warehouse_name || "").trim())) {
      toast.error(`Warehouse is required for ${_ps} procurement rows.`);
      return;
    }
    setBusy(true);
    try {
      if (editing.id && editing.source === "standalone") {
        const { data } = await api.patch(`/procurement/${editing.id}`, editing);
        setRows((rs) => rs.map((r) => (r.id === data.id ? _withSourceLabel({ ...data, source: "standalone" }) : r)));
      } else if (editing.id && editing.source === "order") {
        // Update the underlying order
        const { data } = await api.patch(`/orders/${editing.id}`, editing);
        setRows((rs) => rs.map((r) => (r.id === data.id ? _withSourceLabel({ ...r, ...data, source: "order" }) : r)));
      } else {
        await api.post("/procurement", editing);
        await load();
      }
      toast.success("Saved");
      setEditing(null);
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };

  const del = async () => {
    if (!editing?.id || editing.source !== "standalone") return;
    if (!window.confirm("Delete this procurement entry?")) return;
    try {
      await api.delete(`/procurement/${editing.id}`);
      setRows((rs) => rs.filter((r) => r.id !== editing.id));
      setEditing(null);
      toast.success("Deleted");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  // Sep 3 2026 (ticket #243978) — China No. is frozen on order-linked rows
  // once allocated (protects allocation bookkeeping), so it can't travel
  // through the normal Save button for those rows. This dedicated action
  // releases the existing allocation server-side and reassigns cleanly.
  const reassignChinaId = async () => {
    if (!editing?.id || editing.source !== "order") return;
    setBusy(true);
    try {
      const { data } = await api.patch(`/orders/${editing.id}/china-id`, { china_id: editing.china_id ?? "" });
      setEditing((s) => ({ ...s, ...data }));
      setRows((rs) => rs.map((r) => (r.id === data.id ? _withSourceLabel({ ...r, ...data, source: "order" }) : r)));
      toast.success("China No. reassigned");
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };

  const advanceStatus = async (row, nextStatus) => {
    try {
      if (row.source === "standalone") {
        const { data } = await api.patch(`/procurement/${row.id}`, { china_order_status: nextStatus });
        setRows((rs) => rs.map((r) => (r.id === data.id ? _withSourceLabel({ ...data, source: "standalone" }) : r)));
      } else {
        const { data } = await api.patch(`/orders/${row.id}`, { china_order_status: nextStatus });
        setRows((rs) => rs.map((r) => (r.id === data.id ? _withSourceLabel({ ...r, china_order_status: data.china_order_status, source: "order" }) : r)));
      }
      toast.success(`Marked ${nextStatus}`);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const nextStatus = (s) => {
    const idx = STATUSES.indexOf(s);
    if (idx < 0 || idx >= STATUSES.length - 2) return null; // Cancelled at end, Delivered = last actionable
    return STATUSES[idx + 1];
  };

  const openSkuTimeline = async (sku) => {
    if (!sku) { toast.error("No System SKU on this row"); return; }
    try {
      const { data } = await api.get(`/procurement/sku/${encodeURIComponent(sku)}/timeline`);
      setSkuTimeline(data);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <>
      <PageHeader title="Procurement" subtitle="China procurement pipeline — orders + standalone" testId={T.moduleTitle("procurement")} module="procurement">
        <div className="relative">
          <Search size={13} className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            data-testid={T.searchInput("procurement")}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && load()}
            placeholder="Search SKU, product, order id…"
            className="h-8 pl-7 pr-2 w-64 text-[13px] border border-slate-200 rounded-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
          />
        </div>
        {/* Mar 1 2026 — Wide / Compact density toggle (same pattern as Orders). */}
        <div className="inline-flex border border-slate-200 rounded-sm overflow-hidden h-8" data-testid="procurement-density-toggle">
          <button
            onClick={() => setDensityPersist("compact")}
            data-testid="procurement-density-compact"
            className={`h-8 px-3 text-[12px] font-semibold inline-flex items-center gap-1.5 ${density === "compact" ? "bg-slate-900 text-white" : "bg-white text-slate-700 hover:bg-slate-50"}`}
          >Compact</button>
          <button
            onClick={() => setDensityPersist("wide")}
            data-testid="procurement-density-wide"
            className={`h-8 px-3 text-[12px] font-semibold inline-flex items-center gap-1.5 border-l border-slate-200 ${density === "wide" ? "bg-slate-900 text-white" : "bg-white text-slate-700 hover:bg-slate-50"}`}
          >Wide</button>
        </div>
        {/* Mar 1 2026 — Warehouse filter (server-side, same pattern as Orders).
            The option list is narrowed by `allowedWarehouses()` so a user
            with a warehouse allowlist never sees warehouses they can't act on. */}
        <MultiSelectFilter
          label="Warehouse"
          options={(() => {
            const all = warehouses.map((w) => w.name).filter(Boolean);
            const allow = allowedWarehouses();
            return allow ? all.filter((n) => allow.includes(n)) : all;
          })()}
          value={warehouseFilter}
          onChange={(next) => setWarehouseFilter(next)}
          testId="procurement-filter-warehouse"
        />
        {/* Mar 3 2026 — Sort dropdown. Applies server-side. */}
        <select
          value={sortBy}
          onChange={(e) => setSortBy(e.target.value)}
          data-testid="procurement-sort"
          className="h-8 px-2 text-[12px] font-medium border border-slate-200 rounded-sm bg-white text-slate-800 focus:outline-none focus:ring-1 focus:ring-blue-500"
        >
          {SORT_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
        {editable && (
          <Button variant="primary" onClick={() => setEditing(emptyRow())} testId={T.newBtn("procurement")}>
            <Plus size={13} /> New Procurement
          </Button>
        )}
        <Button onClick={doTemplate} testId={T.exportBtn("procurement-template")}>
          <Download size={13} /> Template
        </Button>
        <Button onClick={doExport} testId={T.exportBtn("procurement")}>
          <Download size={13} /> Export
        </Button>
        {editable && (
          <label className="inline-flex items-center gap-1 h-8 px-2 text-[12px] font-medium border border-slate-200 rounded-sm cursor-pointer hover:bg-slate-50" data-testid={T.importBtn("procurement")}>
            <Upload size={13} /> Import
            <input type="file" accept=".csv,.xls,.xlsx,.txt" onChange={doImport} className="hidden" />
          </label>
        )}
        <Button onClick={() => setShowFilterBar((s) => !s)} testId="filter-procurement-btn">
          <Filter size={13} /> Filters
          {activeAdvCount > 0 && (
            <span className="ml-1 inline-flex items-center justify-center min-w-[16px] h-4 px-1 text-[9.5px] font-bold text-white bg-blue-600 rounded-full">
              {activeAdvCount}
            </span>
          )}
        </Button>
        <FilterPresets
          moduleKey="procurement"
          value={advFilters}
          onLoad={setAdvFilters}
          extra={{ revealedCols }}
          onLoadExtra={(x) => setRevealedCols(x?.revealedCols || [])}
        />
        {isAdmin && selectedIds.size > 0 && (
          <Button
            variant="danger"
            onClick={() => { setConfirmDeleteSelected(true); setConfirmDeleteText(""); }}
            testId="procurement-delete-selected-btn"
            title={`Permanently delete ${selectedIds.size} selected procurement row${selectedIds.size === 1 ? "" : "s"}`}
          >
            <AlertTriangle size={13} /> Delete Selected · {selectedIds.size}
          </Button>
        )}
        {isAdmin && (
          <Button variant="danger" onClick={doWipe} testId="wipe-procurement-btn">
            <AlertTriangle size={13} /> Wipe All
          </Button>
        )}
      </PageHeader>
      <PageBody>
        {canBulkEdit("procurement") && (
          <div className="mb-2 border border-blue-200 bg-blue-50/60 rounded-sm px-3 py-2 flex flex-wrap items-center gap-2" data-testid="procurement-bulk-update-bar">
            <span className="text-[11px] uppercase tracking-wider text-blue-900 font-bold shrink-0">Bulk Update</span>
            <select
              data-testid="proc-bulk-field-select"
              value={bulkField}
              onChange={(e) => { setBulkField(e.target.value); setBulkValue(""); }}
              className="h-8 px-2 text-[12.5px] border border-slate-200 rounded-sm bg-white min-w-[190px]"
            >
              <option value="">Select field</option>
              {Object.entries(BULK_FIELD_LABELS).map(([k, lbl]) => (
                <option key={k} value={k}>{lbl}</option>
              ))}
            </select>
            {bulkField && (() => {
              const spec = BULK_FIELD_SPECS[bulkField];
              if (spec?.type === "select") {
                return (
                  <select data-testid="proc-bulk-value-select" value={bulkValue}
                    onChange={(e) => setBulkValue(e.target.value)}
                    className="h-8 px-2 text-[12.5px] border border-slate-200 rounded-sm bg-white min-w-[160px]">
                    <option value="">Select value</option>
                    {(spec.options || []).map((opt) => <option key={opt} value={opt}>{opt}</option>)}
                  </select>
                );
              }
              return (
                <input
                  data-testid="proc-bulk-value-input"
                  type={spec?.type === "number" ? "number" : (spec?.type === "date" ? "date" : "text")}
                  step={spec?.type === "number" ? "any" : undefined}
                  value={bulkValue}
                  onChange={(e) => setBulkValue(e.target.value)}
                  placeholder={spec?.type === "date" ? "yyyy-mm-dd" : "Value"}
                  className="h-8 px-2 text-[12.5px] border border-slate-200 rounded-sm bg-white min-w-[160px]"
                />
              );
            })()}
            <div className="inline-flex h-8 items-center border border-slate-200 rounded-sm bg-white overflow-hidden">
              <button type="button" data-testid="proc-bulk-mode-all"
                onClick={() => setBulkMode("all")}
                className={`h-full px-3 text-[12px] font-semibold ${bulkMode === "all" ? "bg-slate-900 text-white" : "bg-white text-slate-700 hover:bg-slate-50"}`}
                title="Update every currently visible procurement row"
              >Update all</button>
              <button type="button" data-testid="proc-bulk-mode-blanks"
                onClick={() => setBulkMode("blanks")}
                className={`h-full px-3 text-[12px] font-semibold border-l border-slate-200 ${bulkMode === "blanks" ? "bg-slate-900 text-white" : "bg-white text-slate-700 hover:bg-slate-50"}`}
                title="Only update visible rows where this field is currently blank"
              >Fill blanks only</button>
            </div>
            <Button
              variant="primary"
              onClick={runBulkPreview}
              testId="proc-bulk-apply-btn"
              disabled={bulkRunning || !bulkField}
            >
              {bulkRunning ? "…" : `Set for ${filteredRows.length} filtered row${filteredRows.length === 1 ? "" : "s"}`}
            </Button>
          </div>
        )}
        <div className="flex gap-2 mb-4 flex-wrap">
          <button
            onClick={() => setStatusFilter("")}
            data-testid="proc-filter-all"
            className={`h-8 px-3 text-[12px] border rounded-sm ${!statusFilter ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-700 border-slate-200 hover:bg-slate-50"}`}
          >
            All <span className="ml-1 font-mono opacity-70">({counts.total})</span>
          </button>
          {STATUSES.map((s) => (
            <button
              key={s}
              data-testid={`proc-filter-${s}`}
              onClick={() => setStatusFilter(s)}
              className={`h-8 px-3 text-[12px] border rounded-sm ${statusFilter === s ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-700 border-slate-200 hover:bg-slate-50"}`}
            >
              {s} <span className="ml-1 font-mono opacity-70">({counts[s] || 0})</span>
            </button>
          ))}
        </div>

        {showFilterBar && (
          <div className="mb-3">
            <FieldFilterBar
              moduleKey="procurement"
              fields={PROCUREMENT_FILTER_FIELDS}
              dropdowns={filterDropdowns}
              value={advFilters}
              onChange={setAdvFilters}
              revealedColumns={revealedCols}
              onToggleColumn={toggleRevealCol}
              rows={rows}
            />
          </div>
        )}

        <div className="border border-slate-200 bg-white rounded-sm overflow-hidden">
          <div className="max-h-[calc(100vh-260px)] overflow-auto">
            {(() => {
              const BASE_COLS = ["system_sku", "product_name", "marketplace_identifier",
                "china_order_status", "china_qty_to_order", "china_price", "sea_price",
                "source_label",
                "china_weight_gram", "china_order_date", "china_shipment_id", "china_comments"];
              const extraCols = revealedCols
                .filter((k) => !BASE_COLS.includes(k))
                .map((k) => PROCUREMENT_FILTER_FIELDS.find((f) => f.key === k))
                .filter(Boolean);
const renderRow = (r) => {
                const nxt = nextStatus(r.china_order_status);
                return (
                    <tr key={`${r.source}-${r.id}`} data-testid={`proc-row-${r.id}`} onClick={() => setEditing({ ...r })} className={`cursor-pointer ${selectedIds.has(r.id) ? "bg-blue-50/60" : ""}`}>
                      {isAdmin && (
                        <td className="text-center" onClick={(e) => e.stopPropagation()}>
                          {r.source === "order" ? (
                            <span
                              className="inline-block w-3.5 h-3.5 border border-slate-200 bg-slate-50 rounded-sm cursor-not-allowed"
                              title="Order-linked row — delete via the Orders module"
                              data-testid={`procurement-select-${r.id}-disabled`}
                            />
                          ) : (
                            <input
                              type="checkbox"
                              data-testid={`procurement-select-${r.id}`}
                              checked={selectedIds.has(r.id)}
                              onChange={(e) => {
                                const next = new Set(selectedIds);
                                if (e.target.checked) next.add(r.id);
                                else next.delete(r.id);
                                setSelectedIds(next);
                              }}
                              className="cursor-pointer"
                            />
                          )}
                        </td>
                      )}
                      {/* Mar 3 2026 — China ID (a.k.a. China No.) surfaced as a
                          top-level column. Previously only visible in the edit
                          drawer header. */}
                      <td className="font-mono font-semibold text-slate-800" data-testid={`proc-china-id-${r.id}`}>
                        {r.china_id ?? "—"}
                      </td>
                      {/* Image cell — thumbnail on top; click to edit image_url (Mar 1 2026 inline spec). */}
                      <InlineCell
                        testId={`proc-inline-image_url-${r.id}`}
                        type="text"
                        value={r.image_url || ""}
                        editable={editable}
                        onSave={(v) => saveInline(r, "image_url", v)}
                        render={() => r.image_url
                          ? <ImageThumb src={r.image_url} size={64} testId={`proc-thumb-${r.id}`} />
                          : <span className="text-slate-400">—</span>}
                      />
                      <td className="font-mono">{r.system_sku || "—"}</td>
                      <td className="truncate max-w-[220px]">{r.product_name || "—"}</td>
                      <td className="font-mono" onClick={(e) => e.stopPropagation()}>
                        {r.marketplace_identifier ? (
                          <span className="inline-flex items-center gap-1">
                            <span>{r.marketplace_identifier}</span>
                            <button
                              type="button"
                              data-testid={`proc-copy-mpid-${r.id}`}
                              onClick={() => copyToClipboard(r.marketplace_identifier, "Marketplace ID")}
                              className="inline-flex items-center justify-center w-4 h-4 text-slate-400 hover:text-indigo-700 hover:bg-indigo-50 rounded-sm"
                              title="Copy Marketplace ID"
                            >
                              <Copy size={11} />
                            </button>
                          </span>
                        ) : "—"}
                      </td>
                      {/* Status — dropdown, PATCH on change. */}
                      <InlineCell
                        testId={`proc-inline-status-${r.id}`}
                        type="select"
                        options={dropdowns.china_order_status || STATUSES}
                        value={r.china_order_status || ""}
                        editable={editable}
                        onSave={(v) => saveInline(r, "china_order_status", v)}
                        render={() => <StatusPill value={r.china_order_status} tone={statusTone(r.china_order_status)} />}
                      />
                      <InlineCell testId={`proc-inline-qty-${r.id}`} type="number" mono
                        value={r.china_qty_to_order ?? ""}
                        editable={editable}
                        onSave={(v) => saveInline(r, "china_qty_to_order", v)} />
                      {/* Mar 2 2026 — Shipped Qty. Defaulted on server to
                          china_qty_to_order at creation but edits stay
                          independent. */}
                      <InlineCell testId={`proc-inline-shipped_qty-${r.id}`} type="number" mono
                        value={r.shipped_qty ?? ""}
                        editable={editable}
                        onSave={(v) => saveInline(r, "shipped_qty", v)} />
                      <InlineCell testId={`proc-inline-china_price-${r.id}`} type="number" mono
                        value={r.china_price ?? ""}
                        editable={editable}
                        onSave={(v) => saveInline(r, "china_price", v)}
                        render={(v) => v === null || v === undefined || v === "" ? <span className="text-slate-400">—</span> : `¥${v}`}
                      />
                      {/* Feb 27 2026 — Sea Price column, next to China Price. */}
                      <InlineCell testId={`proc-inline-sea_price-${r.id}`} type="number" mono
                        value={r.sea_price ?? ""}
                        editable={editable}
                        onSave={(v) => saveInline(r, "sea_price", v)}
                        render={(v) => v === null || v === undefined || v === "" ? <span className="text-slate-400">—</span> : `¥${v}`}
                      />
                      {/* Feb 27 2026 — unified Source column with 3 values:
                          Order (auto — order-linked row, read-only), Direct
                          / Sea (user-selected via procurement_source on
                          standalone rows). */}
                      <InlineCell
                        testId={`proc-inline-source_label-${r.id}`}
                        type="select"
                        options={["Direct", "Sea"]}
                        value={r.procurement_source || ""}
                        editable={editable && r.source !== "order"}
                        onSave={(v) => saveInline(r, "procurement_source", v)}
                        render={() => {
                          const label = r.source_label || (r.source === "order" ? "Order" : (r.procurement_source || "Direct"));
                          const cls = label === "Order" ? "bg-blue-50 text-blue-800 border-blue-200"
                            : label === "Sea" ? "bg-sky-50 text-sky-800 border-sky-200"
                            : "bg-emerald-50 text-emerald-800 border-emerald-200";
                          return <span className={`inline-flex items-center px-1.5 h-5 text-[10px] font-bold uppercase tracking-wider rounded-sm border ${cls}`}>{label}</span>;
                        }}
                      />
                      {density === "wide" && (
                        <InlineCell testId={`proc-inline-china_shipping-${r.id}`} type="number" mono
                          value={r.china_shipping ?? ""}
                          editable={editable}
                          onSave={(v) => saveInline(r, "china_shipping", v)}
                          render={(v) => v === null || v === undefined || v === "" ? <span className="text-slate-400">—</span> : `¥${v}`}
                        />
                      )}
                      <InlineCell testId={`proc-inline-weight-${r.id}`} type="number" mono
                        value={r.china_weight_gram ?? ""}
                        editable={editable}
                        onSave={(v) => saveInline(r, "china_weight_gram", v)} />
                      {density === "wide" && (
                        <InlineCell testId={`proc-inline-china_order_id-${r.id}`} type="text" mono
                          value={r.china_order_id || ""}
                          editable={editable}
                          onSave={(v) => saveInline(r, "china_order_id", v)} />
                      )}
                      <InlineCell testId={`proc-inline-order_date-${r.id}`} type="date" mono
                        value={r.china_order_date || ""}
                        editable={editable}
                        render={(v) => fmtDate(v) || <span className="text-slate-400">—</span>}
                        onSave={(v) => saveInline(r, "china_order_date", v)} />
                      {density === "wide" && (
                        <InlineCell testId={`proc-inline-dispatched-${r.id}`} type="date" mono
                          value={r.china_order_dispatched_date || ""}
                          editable={editable}
                          render={(v) => fmtDate(v) || <span className="text-slate-400">—</span>}
                          onSave={(v) => saveInline(r, "china_order_dispatched_date", v)} />
                      )}
                      {density === "wide" && (
                        <InlineCell testId={`proc-inline-received-${r.id}`} type="date" mono
                          value={r.china_order_received_date || ""}
                          editable={editable}
                          render={(v) => fmtDate(v) || <span className="text-slate-400">—</span>}
                          onSave={(v) => saveInline(r, "china_order_received_date", v)} />
                      )}
                      <InlineCell testId={`proc-inline-shipment_id-${r.id}`} type="text" mono
                        value={r.china_shipment_id || ""}
                        editable={editable}
                        onSave={(v) => saveInline(r, "china_shipment_id", v)}
                        className="text-indigo-800" />
                      <InlineCell testId={`proc-inline-comments-${r.id}`} type="text"
                        value={r.china_comments || ""}
                        editable={editable}
                        onSave={(v) => saveInline(r, "china_comments", v)}
                        className="max-w-[180px] text-slate-600 text-[11.5px]"
                        render={(v) => v ? <TranslateInline text={v} testId={`proc-translate-${r.id}`} /> : <span className="text-slate-400">—</span>}
                      />
                      {/* Mar 3 2026 — Tax Name / Rate / HSN inline-editable cells. */}
                      <InlineCell testId={`proc-inline-tax_name-${r.id}`} type="text"
                        value={r.tax_name || ""}
                        editable={editable}
                        onSave={(v) => saveInline(r, "tax_name", v)}
                        className="max-w-[120px] text-[11.5px]" />
                      <InlineCell testId={`proc-inline-tax_rate-${r.id}`} type="number" mono
                        value={r.tax_rate ?? ""}
                        editable={editable}
                        onSave={(v) => saveInline(r, "tax_rate", v)} />
                      <InlineCell testId={`proc-inline-hsn_code-${r.id}`} type="text" mono
                        value={r.hsn_code || ""}
                        editable={editable}
                        onSave={(v) => saveInline(r, "hsn_code", v)}
                        className="max-w-[100px] text-[11.5px]" />
                      {/* Mar 3 2026 — new fields */}
                      <InlineCell testId={`proc-inline-china_return_price-${r.id}`} type="number" mono
                        value={r.china_return_price ?? ""}
                        editable={editable}
                        onSave={(v) => saveInline(r, "china_return_price", v)}
                        render={(v) => v === null || v === undefined || v === "" ? <span className="text-slate-400">—</span> : `¥${v}`}
                      />
                      <td className="text-right font-mono text-[11.5px] text-slate-800 font-semibold"
                          data-testid={`proc-total-price-${r.id}`}>
                        {r.total_price != null && r.total_price !== "" ? `¥${r.total_price}` : "—"}
                      </td>
                      <InlineCell testId={`proc-inline-package_no-${r.id}`} type="text" mono
                        value={r.package_no || ""}
                        editable={editable}
                        onSave={(v) => saveInline(r, "package_no", v)}
                        className="max-w-[110px] text-[11.5px]" />
                      <td className="font-mono text-[11.5px] text-slate-700"
                          data-testid={`proc-delivered-date-${r.id}`}>
                        {fmtDate(r.delivered_date)}
                      </td>
                      {extraCols.map((c) => (
                        <td key={c.key} className={c.type === "number" ? "font-mono" : ""} data-testid={`proc-extra-td-${c.key}`}>
                          {r[c.key] !== null && r[c.key] !== undefined && r[c.key] !== "" ? String(r[c.key]) : "—"}
                        </td>
                      ))}
                      <td onClick={(e) => e.stopPropagation()}>
                        {r.system_sku && (
                          <div className="inline-flex items-center gap-1">
                            <button
                              data-testid={`proc-history-${r.id}`}
                              onClick={() => openSkuTimeline(r.system_sku)}
                              className="inline-flex items-center gap-1 h-6 px-2 text-[10.5px] font-semibold text-indigo-700 hover:bg-indigo-50 border border-indigo-200 rounded-sm"
                              title={`View procurement history for ${r.system_sku}`}
                            >
                              <History size={11} /> SKU
                            </button>
                            {/* Mar 2 2026 — Split shipment. Only makes sense
                                on rows with a quantity > 1 to actually
                                divide up. */}
                            {editable && r.source === "standalone" && Number(r.china_qty_to_order || 0) > 1 && (
                              <button
                                data-testid={`proc-split-${r.id}`}
                                onClick={() => openSplitModal(r)}
                                className="inline-flex items-center gap-1 h-6 px-2 text-[10.5px] font-semibold text-purple-700 hover:bg-purple-50 border border-purple-200 rounded-sm"
                                title="Split this shipment into multiple rows"
                              >
                                <Split size={11} /> Split
                              </button>
                            )}
                          </div>
                        )}
                      </td>
                      <td onClick={(e) => e.stopPropagation()}>
                        {editable && nxt && (
                          <button
                            data-testid={`proc-advance-${r.id}`}
                            onClick={() => advanceStatus(r, nxt)}
                            className="inline-flex items-center gap-1 h-6 px-2 text-[10.5px] font-semibold uppercase tracking-wider text-indigo-700 hover:bg-indigo-50 border border-indigo-200 rounded-sm"
                            title={`Advance to ${nxt}`}
                          >
                            {nxt === "Dispatched" && <Truck size={11} />}
                            {nxt === "Delivered" && <PackageOpen size={11} />}
                            {nxt} <ArrowRight size={11} />
                          </button>
                        )}
                      </td>
                    </tr>
                );
              };
              return (
            <table className="erp-table">
              <thead>
                <tr>
                  {isAdmin && (() => {
                    const selectablePaged = pagedRows.filter((r) => r.source !== "order");
                    const allSelected = selectablePaged.length > 0 && selectablePaged.every((r) => selectedIds.has(r.id));
                    return (
                      <th className="w-[32px] text-center">
                        <input
                          type="checkbox"
                          data-testid="procurement-select-all"
                          checked={allSelected}
                          onChange={(e) => {
                            const next = new Set(selectedIds);
                            if (e.target.checked) selectablePaged.forEach((r) => next.add(r.id));
                            else selectablePaged.forEach((r) => next.delete(r.id));
                            setSelectedIds(next);
                          }}
                          className="cursor-pointer"
                        />
                      </th>
                    );
                  })()}
                  <th>China ID</th>
                  <th>Image</th>
                  <th>System SKU</th>
                  <th>Product</th>
                  <th>Marketplace ID</th>
                  <th>Status</th>
                  <th>Qty</th>
                  <th>Shipped Qty</th>
                  <th>China Price</th>
                  <th>Sea Price ¥</th>
                  <th>Source</th>
                  {/* Mar 1 2026 — Wide-mode detail columns. Same fields, exposed
                      inline so the Wide/Compact toggle acts like Orders'. */}
                  {density === "wide" && <th>China Shipping</th>}
                  <th>Weight (g)</th>
                  {density === "wide" && <th>China Order ID</th>}
                  <th>Order Date</th>
                  {density === "wide" && <th>Dispatched</th>}
                  {density === "wide" && <th>Received</th>}
                  <th>Shipment ID</th>
                  <th>Comments</th>
                  {/* Mar 3 2026 — Tax metadata (Tax Name / Rate / HSN) shown
                      inline so ops can spot rows missing a tax classification. */}
                  <th>Tax Name</th>
                  <th>Tax %</th>
                  <th>HSN</th>
                  {/* Mar 3 2026 — Return Price / Total Price / Package No / Delivered Date */}
                  <th>Return ¥</th>
                  <th>Total ¥</th>
                  <th>Package No</th>
                  <th>Delivered</th>
                  {extraCols.map((c) => <th key={c.key} data-testid={`proc-extra-th-${c.key}`}>{c.label}</th>)}
                  <th>History</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {pageGroups.flatMap((g) => {
                  if (g.rows.length <= 1) return [renderRow(g.rows[0])];
                  // Sep 3 2026 — china_qty_to_order is the BATCH total on the source
                  // procurement/buy row, not a per-order amount that sums across a group
                  // (support ticket #243978, resurfacing #241179's display fix). Rows
                  // sharing a china_id are one batch split across orders, so the group's
                  // true qty is the max, not the sum.
                  const qtySum = Math.max(...g.rows.map((rr) => Number(rr.china_qty_to_order) || 0));
                  const rep = g.rows[0];
                  // Sep 5 2026 (ticket #245038) — the summary row rendered only the
                  // product name + count inside one colSpan cell, so every other
                  // column — including China ID — silently disappeared once rows
                  // were grouped, even though China ID still shows normally on the
                  // Orders screen (which has no grouping) and on every ungrouped
                  // Procurement row. Surface China ID (and status, so a batch split
                  // across statuses reads as "Mixed" instead of one row's status)
                  // directly on the summary line.
                  // Sep 6 2026 (ticket #243978) — only genuinely different real
                  // statuses should read as split; a row that simply has no status
                  // yet is not a conflict with a sibling that does, so blanks are
                  // excluded before deciding whether the group is "Mixed".
                  const realStatuses = new Set(g.rows.map((rr) => rr.china_order_status).filter(Boolean));
                  const groupStatus =
                    realStatuses.size === 0 ? "—" :
                    realStatuses.size === 1 ? [...realStatuses][0] :
                    [...realStatuses].join(" / ");
                  // Sep 3 2026 — customer asked for strictly one row per china_id on
                  // screen (ticket #243978); per-order detail lives in the XLSX export
                  // only, so the group header is a static summary, not an expandable
                  // toggle.
                  const header = (
                    <tr key={`group-${g.key}`} data-testid={`proc-group-${g.key}`} className="bg-amber-50/70">
                      <td colSpan={19 + (density === "wide" ? 4 : 0) + extraCols.length + (isAdmin ? 1 : 0)}>
                        <span className="inline-flex items-center gap-2 font-semibold text-amber-900">
                          <Layers size={12} className="text-amber-600" />
                          {rep.product_name || rep.system_sku || "Grouped rows"}
                          <span className="font-mono text-[11px] text-amber-700">({g.rows.length} orders · qty {qtySum})</span>
                          {rep.china_id !== null && rep.china_id !== undefined && rep.china_id !== "" && (
                            <span
                              className="font-mono text-[11px] text-amber-800 border border-amber-300 rounded-sm px-1"
                              data-testid={`proc-group-china-id-${g.key}`}
                            >
                              China No. {rep.china_id}
                            </span>
                          )}
                          <span className="text-[11px] text-amber-800" data-testid={`proc-group-status-${g.key}`}>
                            {groupStatus}
                          </span>
                          {g.label && <span className="text-[10px] uppercase tracking-wide text-amber-700 border border-amber-300 rounded-sm px-1">grouped by {g.label}</span>}
                        </span>
                      </td>
                    </tr>
                  );
                  return [header];
                })}
                {!filteredRows.length && (
                  <tr><td colSpan={23 + (density === "wide" ? 4 : 0) + extraCols.length + (isAdmin ? 1 : 0)} className="text-center py-8 text-slate-500">
                    {rows.length > 0
                      ? <>No entries match the current filters. <button onClick={() => setAdvFilters({})} className="text-blue-600 hover:underline">Clear filters</button></>
                      : "No procurement entries."}
                  </td></tr>
                )}
              </tbody>
            </table>
              );
            })()}
          </div>
          <div className="px-3 py-2 border-t border-slate-200 flex items-center justify-end">
            <PaginationBar
              page={page}
              pageSize={pageSize}
              total={filteredRows.length}
              onPageChange={(p) => setPage(p)}
              onPageSizeChange={(s) => { setPageSize(s); setPage(1); }}
              testIdPrefix="procurement"
            />
          </div>
        </div>
      </PageBody>

      {editing && (
        <div className="fixed inset-0 z-50 flex justify-end bg-slate-900/30" onClick={() => setEditing(null)}>
          <div className="w-[640px] max-w-full h-full bg-white border-l border-slate-200 flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="h-14 border-b border-slate-200 flex items-center justify-between px-4">
              <div>
                <div className="font-heading text-[14px] font-semibold">
                  {editing.id ? (editing.source === "order" ? `Order Procurement #${editing.system_id ?? ""}` : "Edit Procurement") : "New Procurement"}
                </div>
                <div className="font-mono text-[11px] text-slate-500">
                  {editing.system_sku || "—"} {editing.china_id ? `• China ID ${editing.china_id}` : ""}
                </div>
              </div>
              <button onClick={() => setEditing(null)} className="text-slate-500 hover:text-slate-900"><X size={16} /></button>
            </div>
            <div className="flex-1 overflow-auto p-4 grid grid-cols-2 gap-3">
              <Field label="System SKU" hidden={!canView("procurement", "system_sku")}>
                <Input testId={T.formInput("system_sku")} value={editing.system_sku || ""} onChange={(e) => setEditing((s) => ({ ...s, system_sku: e.target.value }))} readOnly={editing.source === "order"} className={editing.source === "order" ? "readonly-field font-mono" : "font-mono"} placeholder={isSimplified ? "Optional" : ""} />
              </Field>
              <Field label="Product Name" hidden={!canView("procurement", "product_name")}>
                <Input testId={T.formInput("product_name")} value={editing.product_name || ""} onChange={(e) => setEditing((s) => ({ ...s, product_name: e.target.value }))} />
              </Field>
              <Field label="Source URL" span={2} hidden={isSimplified || !canView("procurement", "source_url")}>
                <Input testId={T.formInput("source_url")} value={editing.source_url || ""} onChange={(e) => setEditing((s) => ({ ...s, source_url: e.target.value }))} />
              </Field>
              <Field label="Variant" hidden={isSimplified || !canView("procurement", "variant")}>
                <Input testId={T.formInput("variant")} value={editing.variant || ""} onChange={(e) => setEditing((s) => ({ ...s, variant: e.target.value }))} />
              </Field>
              <Field label="Qty to Order" hidden={!canView("procurement", "china_qty_to_order")}>
                <Input type="number" testId={T.formInput("china_qty_to_order")} value={editing.china_qty_to_order ?? ""} onChange={(e) => setEditing((s) => ({ ...s, china_qty_to_order: e.target.value === "" ? "" : Number(e.target.value) }))} />
              </Field>
              <Field label="China Price (¥)" hidden={isSimplified || !canView("procurement", "china_price")}>
                <Input type="number" testId={T.formInput("china_price")} value={editing.china_price ?? ""} onChange={(e) => setEditing((s) => ({ ...s, china_price: e.target.value === "" ? "" : Number(e.target.value) }))} />
              </Field>
              {/* Feb 27 2026 — Sea Price (RMB) sits next to China Price. */}
              <Field label="Sea Price (¥)" hidden={isSimplified || !canView("procurement", "sea_price")}>
                <Input type="number" testId={T.formInput("sea_price")} value={editing.sea_price ?? ""} onChange={(e) => setEditing((s) => ({ ...s, sea_price: e.target.value === "" ? "" : Number(e.target.value) }))} />
              </Field>
              {/* Feb 27 2026 — Direct / Sea toggle. */}
              <Field label="Procurement Source" hidden={!canView("procurement", "procurement_source")}>
                <Select
                  testId={T.formInput("procurement_source")}
                  options={dropdowns.procurement_source || ["Direct", "Sea"]}
                  value={editing.procurement_source || ""}
                  onChange={(v) => setEditing((s) => ({ ...s, procurement_source: v }))}
                />
              </Field>
              {/* Sep 20 2026 — Warehouse dropdown (required for Direct / Sea
                  standalone rows so In-Transit aggregation attributes the
                  row correctly; hidden for order-linked rows since those
                  inherit the destination warehouse from account_id). */}
              {editing.source !== "order" && (
                <Field
                  label={((editing.procurement_source || "").trim() === "Direct"
                          || (editing.procurement_source || "").trim() === "Sea")
                          ? "Warehouse *" : "Warehouse"}
                  hidden={!canView("procurement", "warehouse_name")}
                >
                  <Select
                    testId={T.formInput("warehouse_name")}
                    options={(() => {
                      const all = (warehouses || []).map((w) => w.name).filter(Boolean);
                      const allow = allowedWarehouses();
                      return allow ? all.filter((n) => allow.includes(n)) : all;
                    })()}
                    value={editing.warehouse_name || ""}
                    onChange={(v) => setEditing((s) => ({ ...s, warehouse_name: v }))}
                  />
                </Field>
              )}
              <Field label="China Shipping (¥)" hidden={isSimplified || !canView("procurement", "china_shipping")}>
                <Input type="number" testId={T.formInput("china_shipping")} value={editing.china_shipping ?? ""} onChange={(e) => setEditing((s) => ({ ...s, china_shipping: e.target.value === "" ? "" : Number(e.target.value) }))} />
              </Field>
              <Field label="Weight (g)" hidden={isSimplified || !canView("procurement", "china_weight_gram")}>
                <Input type="number" testId={T.formInput("china_weight_gram")} value={editing.china_weight_gram ?? ""} onChange={(e) => setEditing((s) => ({ ...s, china_weight_gram: e.target.value === "" ? "" : Number(e.target.value) }))} />
              </Field>
              {/* Mar 3 2026 — Simplified mode: Transfer Price is a plain
                  editable input right next to the primary product fields
                  instead of being buried in the read-only Computed section. */}
              {isSimplified && (
                <Field label="Transfer Price ₹" hidden={!canView("procurement", "china_transfer_price")}>
                  <Input
                    type="number"
                    testId={T.formInput("china_transfer_price")}
                    value={editing.china_transfer_price ?? ""}
                    onChange={(e) => setEditing((s) => ({ ...s, china_transfer_price: e.target.value === "" ? "" : Number(e.target.value) }))}
                    className="font-mono"
                  />
                </Field>
              )}
              <Field label="Status" hidden={!canView("procurement", "china_order_status")}>
                <Select testId={T.formInput("china_order_status")} options={dropdowns.china_order_status || STATUSES} value={editing.china_order_status} onChange={(v) => setEditing((s) => ({ ...s, china_order_status: v }))} />
              </Field>
              {/* Sep 3 2026 (ticket #243978) — China No. edit, requested so ops
                  can move an order off a stale/mis-allocated batch. Direct
                  rows save through the normal Save button; order-linked rows
                  go through the dedicated Reassign action since china_id is
                  otherwise frozen once allocated. */}
              <Field label="China No." hidden={!canView("procurement", "china_id")}>
                {editing.source === "order" ? (
                  <div className="flex gap-1.5">
                    <Input
                      type="number"
                      testId={T.formInput("china_id")}
                      value={editing.china_id ?? ""}
                      onChange={(e) => setEditing((s) => ({ ...s, china_id: e.target.value === "" ? "" : Number(e.target.value) }))}
                      className="font-mono"
                    />
                    <button
                      type="button"
                      data-testid={T.formInput("china_id_reassign")}
                      onClick={reassignChinaId}
                      disabled={busy}
                      title="Release any current procurement allocation and reassign this China No."
                      className="h-8 px-2 text-[11px] font-semibold rounded-sm border border-indigo-300 bg-indigo-50 text-indigo-800 hover:bg-indigo-100 whitespace-nowrap"
                    >
                      Reassign
                    </button>
                  </div>
                ) : (
                  <Input
                    type="number"
                    testId={T.formInput("china_id")}
                    value={editing.china_id ?? ""}
                    onChange={(e) => setEditing((s) => ({ ...s, china_id: e.target.value === "" ? "" : Number(e.target.value) }))}
                    className="font-mono"
                  />
                )}
              </Field>
              <Field label="China Order ID" hidden={!canView("procurement", "china_order_id")}>
                <Input testId={T.formInput("china_order_id")} value={editing.china_order_id || ""} onChange={(e) => setEditing((s) => ({ ...s, china_order_id: e.target.value }))} />
              </Field>
              <Field label="Shipment ID" hidden={!canView("procurement", "china_shipment_id")}>
                <Input testId={T.formInput("china_shipment_id")} value={editing.china_shipment_id || ""} onChange={(e) => setEditing((s) => ({ ...s, china_shipment_id: e.target.value }))} />
              </Field>
              <Field label="Order Date" hidden={!canView("procurement", "china_order_date")}>
                <Input type="date" testId={T.formInput("china_order_date")} value={editing.china_order_date || ""} onChange={(e) => setEditing((s) => ({ ...s, china_order_date: e.target.value }))} />
              </Field>
              <Field label="Dispatched Date" hidden={!canView("procurement", "china_order_dispatched_date")}>
                <Input type="date" testId={T.formInput("china_order_dispatched_date")} value={editing.china_order_dispatched_date || ""} onChange={(e) => setEditing((s) => ({ ...s, china_order_dispatched_date: e.target.value }))} />
              </Field>
              <Field label="Received Date" hidden={!canView("procurement", "china_order_received_date")}>
                <Input type="date" testId={T.formInput("china_order_received_date")} value={editing.china_order_received_date || ""} onChange={(e) => setEditing((s) => ({ ...s, china_order_received_date: e.target.value }))} />
              </Field>
              <Field label="Image" span={2} hidden={!canView("procurement", "image_url")}>
                <ImageInput testId={T.formInput("image_url")} value={editing.image_url} onChange={(v) => setEditing((s) => ({ ...s, image_url: v }))} />
              </Field>
              <Field label="Comments" span={2} hidden={isSimplified || !canView("procurement", "china_comments")}>
                <Input testId={T.formInput("china_comments")} value={editing.china_comments || ""} onChange={(e) => setEditing((s) => ({ ...s, china_comments: e.target.value }))} />
              </Field>

              {/* Mar 3 2026 — Tax metadata. Visible for all users (including
                  Simplified) because tax classification is required for
                  invoicing and doesn't depend on the costing pipeline. */}
              <Field label="Tax Name" hidden={!canView("procurement", "tax_name")}>
                <Input testId={T.formInput("tax_name")} value={editing.tax_name || ""} onChange={(e) => setEditing((s) => ({ ...s, tax_name: e.target.value }))} />
              </Field>
              <Field label="Tax Rate %" hidden={!canView("procurement", "tax_rate")}>
                <Input type="number" testId={T.formInput("tax_rate")} value={editing.tax_rate ?? ""} onChange={(e) => setEditing((s) => ({ ...s, tax_rate: e.target.value === "" ? "" : Number(e.target.value) }))} />
              </Field>
              <Field label="HSN Code" hidden={!canView("procurement", "hsn_code")}>
                <Input testId={T.formInput("hsn_code")} value={editing.hsn_code || ""} onChange={(e) => setEditing((s) => ({ ...s, hsn_code: e.target.value }))} className="font-mono" />
              </Field>
              {/* Mar 3 2026 — Return Price + Total Price + Package No + Delivered Date */}
              <Field label="Return Price ¥" hidden={!canView("procurement", "china_return_price")}>
                <Input type="number" testId={T.formInput("china_return_price")}
                  value={editing.china_return_price ?? ""}
                  onChange={(e) => setEditing((s) => ({ ...s, china_return_price: e.target.value === "" ? "" : Number(e.target.value) }))} />
              </Field>
              <Field label="Total Price ¥" hidden={!canView("procurement", "total_price")}>
                <Input type="text" testId={T.formInput("total_price")}
                  value={editing.total_price != null && editing.total_price !== "" ? `¥${editing.total_price}` : ""}
                  readOnly className="readonly-field font-mono" />
              </Field>
              <Field label="Package No" hidden={!canView("procurement", "package_no")}>
                <Input testId={T.formInput("package_no")}
                  value={editing.package_no || ""}
                  onChange={(e) => setEditing((s) => ({ ...s, package_no: e.target.value }))}
                  className="font-mono" />
              </Field>
              <Field label="Delivered Date" hidden={!canView("procurement", "delivered_date")}>
                <Input testId={T.formInput("delivered_date")}
                  value={fmtDate(editing.delivered_date)}
                  readOnly className="readonly-field font-mono"
                  placeholder="Auto-set on Delivered" />
              </Field>

              {/* Mar 3 2026 — Account ID is required in both modes. Kept
                  outside the Costing-inputs block so Simplified users still
                  see it after the block is hidden below. */}
              <Field label="Account ID" hidden={!canView("procurement", "account_id")}>
                <Select
                  testId={T.formInput("account_id")}
                  value={editing.account_id || ""}
                  onChange={(v) => setEditing((s) => ({ ...s, account_id: v }))}
                  options={
                    (isAdmin
                      ? accounts
                      : accounts.filter((a) => (user?.account_ids || []).includes(a.account_id))
                    ).map((a) => a.account_id).filter(Boolean).sort()
                  }
                />
              </Field>

              {/* Feb 25 2026 spec — settings-snapshot INPUTS plus their
                  compute_china_fields OUTPUTS on the Procurement form.
                  Inputs are editable; outputs are readonly and refresh on
                  every save via the backend's compute_china_fields hook.
                  Mar 3 2026 — hidden entirely for Simplified users, who
                  edit transfer_price directly above and never touch the
                  cost formulas. */}
              {!isSimplified && (<>
              <div className="col-span-2 border-t border-slate-200 mt-1 pt-3 text-[10.5px] uppercase tracking-wider font-bold text-slate-500">
                Costing inputs
              </div>
              <Field label="Electrical / Non-Electrical" hidden={!canView("procurement", "electrical_type")}>
                <Select testId={T.formInput("electrical_type")} options={["Electrical", "Non-Electrical"]} value={editing.electrical_type || ""} onChange={(v) => setEditing((s) => ({ ...s, electrical_type: v }))} />
              </Field>
              <Field label="GST Rate %" hidden={!canView("procurement", "gst_rate")}>
                <Input type="number" testId={T.formInput("gst_rate")} value={editing.gst_rate ?? ""} onChange={(e) => setEditing((s) => ({ ...s, gst_rate: e.target.value === "" ? "" : Number(e.target.value) }))} />
              </Field>
              <Field label="RMB Conversion" hidden={!canView("procurement", "global_rmb_conversion_rate")}>
                <Input type="number" testId={T.formInput("global_rmb_conversion_rate")} value={editing.global_rmb_conversion_rate ?? ""} onChange={(e) => setEditing((s) => ({ ...s, global_rmb_conversion_rate: e.target.value === "" ? "" : Number(e.target.value) }))} />
              </Field>
              <Field label="Intl Freight Charges" hidden={!canView("procurement", "international_freight_rate")}>
                <Input type="number" testId={T.formInput("international_freight_rate")} value={editing.international_freight_rate ?? ""} onChange={(e) => setEditing((s) => ({ ...s, international_freight_rate: e.target.value === "" ? "" : Number(e.target.value) }))} />
              </Field>
              <Field label="Aggregator Commission %" hidden={!canView("procurement", "aggregator_commission_percentage")}>
                <Input type="number" testId={T.formInput("aggregator_commission_percentage")} value={editing.aggregator_commission_percentage ?? ""} onChange={(e) => setEditing((s) => ({ ...s, aggregator_commission_percentage: e.target.value === "" ? "" : Number(e.target.value) }))} />
              </Field>
              <Field label="Importrend Commission %" hidden={!canView("procurement", "china_margin_percentage")}>
                <Input type="number" testId={T.formInput("china_margin_percentage")} value={editing.china_margin_percentage ?? ""} onChange={(e) => setEditing((s) => ({ ...s, china_margin_percentage: e.target.value === "" ? "" : Number(e.target.value) }))} />
              </Field>

              <div className="col-span-2 border-t border-slate-200 mt-1 pt-3 text-[10.5px] uppercase tracking-wider font-bold text-slate-500">
                Computed (recalculated on save)
              </div>
              <Field label="China Costing ₹" hidden={!canView("procurement", "china_costing")}>
                <Input testId={T.formInput("china_costing")} value={editing.china_costing ?? ""} readOnly className="readonly-field font-mono" />
              </Field>
              <Field label="China Intl Freight ₹" hidden={!canView("procurement", "china_international_freight_charges")}>
                <Input testId={T.formInput("china_international_freight_charges")} value={editing.china_international_freight_charges ?? ""} readOnly className="readonly-field font-mono" />
              </Field>
              <Field label="Aggregator Commission ₹" hidden={!canView("procurement", "china_aggregator_margin")}>
                <Input testId={T.formInput("china_aggregator_margin")} value={editing.china_aggregator_margin ?? ""} readOnly className="readonly-field font-mono" />
              </Field>
              <Field label="GST ₹" hidden={!canView("procurement", "china_gst")}>
                <Input testId={T.formInput("china_gst")} value={editing.china_gst ?? ""} readOnly className="readonly-field font-mono" />
              </Field>
              <Field label="Landing Price ₹" hidden={!canView("procurement", "china_landing_price")}>
                <Input testId={T.formInput("china_landing_price")} value={editing.china_landing_price ?? ""} readOnly className="readonly-field font-mono" />
              </Field>
              <Field label="Importrend Commission ₹" hidden={!canView("procurement", "china_margin")}>
                <Input testId={T.formInput("china_margin")} value={editing.china_margin ?? ""} readOnly className="readonly-field font-mono" />
              </Field>
              <Field label="Importrend GST ₹" hidden={!canView("procurement", "china_margin_gst")}>
                <Input testId={T.formInput("china_margin_gst")} value={editing.china_margin_gst ?? ""} readOnly className="readonly-field font-mono" />
              </Field>
              <Field label={
                editing.china_transfer_price_manual ? "Transfer Price ₹ (manual)" : "Transfer Price ₹"
              } hidden={!canView("procurement", "china_transfer_price")}>
                {/* Sep 20 2026 — admin / edit-access users can hand-edit
                    Transfer Price; the manual value latches
                    china_transfer_price_manual=True on the server and every
                    compute path skips the TP write while that flag holds.
                    Clearing the field un-latches it back to the auto value.
                    Simplified users still get the top-of-form input above,
                    so this readOnly branch stays intact for view-only users
                    who lack edit access. */}
                <Input
                  testId={T.formInput("china_transfer_price")}
                  type="number"
                  value={editing.china_transfer_price ?? ""}
                  onChange={editable ? (e) => setEditing((s) => ({
                    ...s,
                    china_transfer_price: e.target.value === "" ? "" : Number(e.target.value),
                    // Keep the local flag in sync so the UI reflects the
                    // latched-vs-auto state immediately; the server does the
                    // authoritative write on Save.
                    china_transfer_price_manual: e.target.value !== "",
                  })) : undefined}
                  readOnly={!editable}
                  className={editable ? "font-mono" : "readonly-field font-mono"}
                />
                {editable && editing.china_transfer_price_manual && (
                  <div className="text-[10.5px] text-amber-700 mt-1 leading-tight">
                    Manually set — auto-recalculation is disabled until this field is cleared.
                  </div>
                )}
                {editable && !editing.china_transfer_price_manual
                        && Number(editing.auto_populated_transfer_price || 0) > 0 && (
                  <div className="text-[10.5px] text-slate-500 mt-1 leading-tight font-mono">
                    Auto: ₹{editing.auto_populated_transfer_price}
                  </div>
                )}
              </Field>
              </>)}

              {editing._missing_fields?.length > 0 && !isSimplified && (
                <div className="col-span-2 rounded-sm border border-amber-200 bg-amber-50 text-amber-900 px-3 py-2 text-[11.5px]">
                  Missing fields: <span className="font-mono">{editing._missing_fields.join(", ")}</span>
                </div>
              )}
            </div>
            <div className="h-14 border-t border-slate-200 flex items-center justify-between px-4">
              <div className="flex gap-2">
                {editing.id && editing.source === "standalone" && editable && (
                  <Button variant="danger" onClick={del} testId={T.deleteBtn}>Delete</Button>
                )}
                {editing.system_sku && (
                  <Button onClick={() => openSkuTimeline(editing.system_sku)} testId="proc-drawer-history-btn">
                    <History size={13} /> SKU history
                  </Button>
                )}
                {editing.system_sku && (
                  <ForecastButton
                    sku={editing.system_sku}
                    testId="proc-drawer-forecast-btn"
                    onApply={(qty) => setEditing((s) => ({ ...s, china_qty_to_order: qty }))}
                  />
                )}
              </div>
              <div className="flex gap-2">
                <Button onClick={() => setEditing(null)} testId={T.cancelBtn}>Cancel</Button>
                {editable && <Button variant="primary" onClick={save} testId={T.saveBtn} disabled={busy}>{busy ? "Saving…" : "Save"}</Button>}
              </div>
            </div>
          </div>
        </div>
      )}
      {skuTimeline && (
        <div className="fixed inset-0 z-[60] flex justify-end bg-slate-900/30" onClick={() => setSkuTimeline(null)}>
          <div className="w-[900px] max-w-full h-full bg-white border-l border-slate-200 flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="h-14 border-b border-slate-200 flex items-center justify-between px-4">
              <div>
                <div className="font-heading text-[14px] font-semibold flex items-center gap-2">
                  <Package size={15} className="text-indigo-600" /> SKU Procurement Timeline
                </div>
                <div className="font-mono text-[11px] text-slate-500">{skuTimeline.system_sku}</div>
              </div>
              <button onClick={() => setSkuTimeline(null)} className="text-slate-500 hover:text-slate-900"><X size={16} /></button>
            </div>
            {skuTimeline.summary && (
              <div className="grid grid-cols-4 gap-2 p-4 bg-slate-50 border-b border-slate-200">
                <div className="bg-white border border-slate-200 rounded-sm p-2">
                  <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Events</div>
                  <div className="font-mono text-lg text-slate-900 font-bold">{skuTimeline.summary.events}</div>
                </div>
                <div className="bg-white border border-slate-200 rounded-sm p-2">
                  <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Avg Transit</div>
                  <div className="font-mono text-lg text-blue-800 font-bold">{skuTimeline.summary.avg_transit_days ?? "—"}<span className="text-[10px] font-normal text-slate-500 ml-1">days</span></div>
                </div>
                <div className="bg-white border border-slate-200 rounded-sm p-2">
                  <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Avg Placed→Recv</div>
                  <div className="font-mono text-lg text-indigo-800 font-bold">{skuTimeline.summary.avg_total_days ?? "—"}<span className="text-[10px] font-normal text-slate-500 ml-1">days</span></div>
                </div>
                <div className="bg-white border border-slate-200 rounded-sm p-2">
                  <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Last Transit</div>
                  <div className="font-mono text-lg text-emerald-800 font-bold">{skuTimeline.summary.last_transit_days ?? "—"}<span className="text-[10px] font-normal text-slate-500 ml-1">days</span></div>
                </div>
              </div>
            )}
            <SkuTimelineTrends events={skuTimeline.events || []} />
            <div className="flex-1 overflow-auto p-4">
              <table className="erp-table" data-testid="proc-sku-timeline-table">
                <thead>
                  <tr>
                    <th>Source</th>
                    <th>System #</th>
                    <th>Status</th>
                    <th>Qty</th>
                    <th>Price ¥</th>
                    <th>Shipping ¥</th>
                    <th>Wt (g)</th>
                    <th>Order Date</th>
                    <th>Dispatched</th>
                    <th>Received</th>
                    <th>Transit</th>
                    <th>Shipment ID</th>
                  </tr>
                </thead>
                <tbody>
                  {(skuTimeline.events || []).map((e) => (
                    <tr key={`${e.source}-${e.id}`}>
                      <td>
                        <span className={`inline-flex px-1.5 h-5 text-[10px] font-bold uppercase tracking-wider rounded-sm border ${e.source === "order" ? "bg-blue-50 text-blue-800 border-blue-200" : "bg-emerald-50 text-emerald-800 border-emerald-200"}`}>
                          {e.source === "order" ? "Order" : "Direct"}
                        </span>
                      </td>
                      <td className="font-mono">{e.system_id ? `#${e.system_id}` : "—"}</td>
                      <td><StatusPill value={e.china_order_status} tone={statusTone(e.china_order_status)} /></td>
                      <td className="font-mono">{e.china_qty_to_order ?? "—"}</td>
                      <td className="font-mono">{e.china_price ?? "—"}</td>
                      <td className="font-mono">{e.china_shipping ?? "—"}</td>
                      <td className="font-mono">{e.china_weight_gram ?? "—"}</td>
                      <td className="font-mono">{e.china_order_date || "—"}</td>
                      <td className="font-mono">{e.china_order_dispatched_date || "—"}</td>
                      <td className="font-mono">{e.china_order_received_date || "—"}</td>
                      <td className="font-mono text-blue-800 font-semibold">{e.transit_days !== null && e.transit_days !== undefined ? `${e.transit_days}d` : "—"}</td>
                      <td className="font-mono text-indigo-800">{e.china_shipment_id || "—"}</td>
                    </tr>
                  ))}
                  {!(skuTimeline.events || []).length && (
                    <tr><td colSpan={12} className="text-center py-6 text-slate-500">No procurement events yet for this SKU.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
      {confirmDeleteSelected && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => !deletingSelected && setConfirmDeleteSelected(false)} data-testid="procurement-delete-selected-modal">
          <div className="bg-white rounded-sm border-2 border-red-500 shadow-2xl max-w-md w-full" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-2 border-b border-red-200 bg-red-50 px-4 py-3">
              <AlertTriangle size={18} className="text-red-700" />
              <h2 className="font-heading text-[14px] font-bold text-red-900">Delete Selected Procurement</h2>
            </div>
            <div className="p-4 space-y-3 text-[12.5px] text-slate-700">
              <p>This will <b className="text-red-700">permanently delete {selectedIds.size} selected standalone procurement row{selectedIds.size === 1 ? "" : "s"}</b>. This action cannot be undone.</p>
              <label className="block">
                <span className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">Type <span className="font-mono text-red-700 bg-red-50 px-1 rounded">DELETE</span> to confirm</span>
                <input
                  autoFocus
                  value={confirmDeleteText}
                  onChange={(e) => setConfirmDeleteText(e.target.value)}
                  disabled={deletingSelected}
                  data-testid="procurement-delete-selected-confirm-input"
                  placeholder="DELETE"
                  className="mt-1 w-full h-8 px-2 text-[12.5px] font-mono border border-slate-200 rounded-sm focus:outline-none focus:ring-2 focus:ring-red-500"
                />
              </label>
            </div>
            <div className="flex justify-end gap-2 border-t border-slate-200 px-4 py-3 bg-slate-50">
              <Button onClick={() => { setConfirmDeleteSelected(false); setConfirmDeleteText(""); }} disabled={deletingSelected} testId="procurement-delete-selected-cancel">Cancel</Button>
              <button
                data-testid="procurement-delete-selected-confirm-btn"
                disabled={deletingSelected || confirmDeleteText !== "DELETE"}
                onClick={async () => {
                  setDeletingSelected(true);
                  try {
                    const { data } = await api.post("/procurement/admin/bulk-delete", {
                      ids: [...selectedIds],
                      confirm: "DELETE",
                    });
                    toast.success(`Deleted ${data.deleted} procurement row${data.deleted === 1 ? "" : "s"}`);
                    setSelectedIds(new Set());
                    setConfirmDeleteSelected(false);
                    setConfirmDeleteText("");
                    load();
                  } catch (e) { toast.error(formatApiError(e)); }
                  finally { setDeletingSelected(false); }
                }}
                className="h-8 px-3 text-[12px] font-bold uppercase tracking-wider bg-red-600 text-white rounded-sm hover:bg-red-700 disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center gap-1"
              >
                <AlertTriangle size={12} /> {deletingSelected ? "Deleting…" : `Delete ${selectedIds.size}`}
              </button>
            </div>
          </div>
        </div>
      )}
      {bulkConfirm && (        <div className="fixed inset-0 z-50 bg-slate-900/40 flex items-start justify-center p-6" data-testid="proc-bulk-confirm">
          <div className="bg-white w-[520px] max-w-full rounded-sm shadow-lg border border-slate-200">
            <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between">
              <div className="text-[13px] font-bold text-slate-800">Confirm bulk update</div>
              <button onClick={() => setBulkConfirm(null)} className="text-slate-500 hover:text-slate-800" data-testid="proc-bulk-confirm-close"><X size={16} /></button>
            </div>
            <div className="px-4 py-4 text-[12.5px] text-slate-700">
              Update <b>{BULK_FIELD_LABELS[bulkField]}</b> to <b className="font-mono text-blue-700">{String(bulkValue) || "(blank)"}</b> for <b className="text-blue-700" data-testid="proc-bulk-confirm-count">{bulkConfirm.count}</b> row{bulkConfirm.count === 1 ? "" : "s"}?
              <div className="mt-2 text-[11px] text-slate-500">Scope: {bulkConfirm.mode === "blanks" ? "only rows where this field is currently blank" : "every currently filtered row"}.</div>
              {bulkConfirm.count === 0 && (
                <div className="mt-2 text-[11.5px] text-amber-700">No rows match — nothing to update.</div>
              )}
            </div>
            <div className="px-4 py-3 border-t border-slate-200 flex justify-end gap-2">
              <button type="button" onClick={() => setBulkConfirm(null)}
                data-testid="proc-bulk-confirm-cancel"
                className="h-8 px-3 text-[12px] font-semibold rounded-sm border border-slate-200 hover:bg-slate-50">
                Cancel
              </button>
              <button type="button" onClick={runBulkApply}
                data-testid="proc-bulk-confirm-apply"
                disabled={bulkRunning || bulkConfirm.count === 0}
                className="h-8 px-3 text-[12px] font-semibold rounded-sm bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50">
                {bulkRunning ? "Updating…" : `Update ${bulkConfirm.count}`}
              </button>
            </div>
          </div>
        </div>
      )}
      {splitFor && (() => {
        const sumQty = splitDrafts.reduce((s, d) => s + Number(d.quantity || 0), 0);
        const originalQty = Number(splitFor.china_qty_to_order || 0);
        const qtyMatches = Math.abs(sumQty - originalQty) < 1e-6;
        return (
          <div className="fixed inset-0 z-50 bg-slate-900/40 flex items-start justify-center p-6" data-testid="proc-split-modal">
            <div className="bg-white w-[640px] max-w-full rounded-sm shadow-lg border border-slate-200 my-8">
              <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between">
                <div>
                  <div className="text-[13px] font-bold text-slate-800">Split Shipment</div>
                  <div className="text-[11px] text-slate-500 mt-0.5 font-mono">
                    {splitFor.china_shipment_id || "—"} · {splitFor.system_sku || "—"} · {splitFor.warehouse_name || "—"} · Total qty {originalQty}
                  </div>
                </div>
                <button onClick={() => { setSplitFor(null); setSplitDrafts([]); }}
                  className="text-slate-500 hover:text-slate-800" data-testid="proc-split-close"><X size={16} /></button>
              </div>
              <div className="px-4 py-4">
                <div className="text-[11px] text-slate-600 mb-2">
                  Split the shipment into multiple rows. The first row keeps the original procurement record; each additional split becomes a new DIRECT row cloning every other field.
                </div>
                <div className="space-y-2 max-h-[50vh] overflow-auto">
                  {splitDrafts.map((d, i) => (
                    <div key={i} className="flex items-center gap-2" data-testid={`proc-split-row-${i}`}>
                      <span className="text-[11px] text-slate-500 w-6 shrink-0">#{i + 1}</span>
                      <input
                        type="text" placeholder="Shipment ID"
                        value={d.china_shipment_id}
                        onChange={(e) => updateSplitDraft(i, { china_shipment_id: e.target.value })}
                        data-testid={`proc-split-shipment-${i}`}
                        className="flex-1 h-8 px-2 text-[12px] border border-slate-200 rounded-sm font-mono focus:outline-none focus:ring-2 focus:ring-purple-500/30 focus:border-purple-400"
                      />
                      <input
                        type="number" min="1" placeholder="Qty"
                        value={d.quantity}
                        onChange={(e) => updateSplitDraft(i, { quantity: e.target.value })}
                        data-testid={`proc-split-qty-${i}`}
                        className="w-24 h-8 px-2 text-[12px] border border-slate-200 rounded-sm font-mono text-right focus:outline-none focus:ring-2 focus:ring-purple-500/30 focus:border-purple-400"
                      />
                      {splitDrafts.length > 2 && (
                        <button onClick={() => removeSplitDraft(i)}
                          data-testid={`proc-split-remove-${i}`}
                          className="text-slate-400 hover:text-rose-600" title="Remove split">
                          <X size={14} />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
                <div className="flex items-center justify-between mt-3 pt-3 border-t border-slate-100">
                  <button onClick={addSplitDraft}
                    data-testid="proc-split-add"
                    className="inline-flex items-center gap-1 h-7 px-2 text-[11px] font-semibold text-purple-700 hover:bg-purple-50 border border-purple-200 rounded-sm">
                    <Plus size={11} /> Add another split
                  </button>
                  <div className={`text-[12px] font-mono ${qtyMatches ? "text-emerald-700" : "text-rose-600"}`} data-testid="proc-split-sum">
                    Sum: {sumQty} / {originalQty}
                    {!qtyMatches && <span className="ml-2">✗ must match</span>}
                    {qtyMatches && <span className="ml-2">✓</span>}
                  </div>
                </div>
              </div>
              <div className="px-4 py-3 border-t border-slate-200 flex justify-end gap-2">
                <button type="button" onClick={() => { setSplitFor(null); setSplitDrafts([]); }}
                  data-testid="proc-split-cancel"
                  className="h-8 px-3 text-[12px] font-semibold rounded-sm border border-slate-200 hover:bg-slate-50">
                  Cancel
                </button>
                <button type="button" onClick={submitSplit}
                  data-testid="proc-split-confirm"
                  disabled={splitBusy || !qtyMatches || splitDrafts.length < 2}
                  className="h-8 px-3 text-[12px] font-semibold rounded-sm bg-purple-600 text-white hover:bg-purple-700 disabled:opacity-50">
                  {splitBusy ? "Splitting…" : `Confirm Split (${splitDrafts.length} rows)`}
                </button>
              </div>
            </div>
          </div>
        );
      })()}


    </>
  );
}
